import { useState, useRef, useEffect } from 'react';
import { ChevronDown } from 'lucide-react';
import styles from './MultiSelectDropdown.module.css';

interface MultiSelectDropdownProps {
  options: { value: string; label: string; isSeparator?: boolean }[];
  selected: string[];
  onChange: (selected: string[]) => void;
  placeholder?: string;
}

export function MultiSelectDropdown({ options, selected, onChange, placeholder = 'Seleccionar...' }: MultiSelectDropdownProps) {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleToggle = (value: string) => {
    if (selected.includes(value)) {
      onChange(selected.filter(v => v !== value));
    } else {
      onChange([...selected, value]);
    }
  };

  const getDisplayText = () => {
    if (selected.length === 0) return placeholder;
    if (selected.length === 1) {
      const option = options.find(o => o.value === selected[0]);
      return option?.label || selected[0];
    }
    return `${selected.length} seleccionados`;
  };

  return (
    <div className={styles.container} ref={dropdownRef}>
      <button
        type="button"
        className={styles.trigger}
        onClick={() => setIsOpen(!isOpen)}
      >
        <span className={styles.triggerText}>{getDisplayText()}</span>
        <ChevronDown className={`${styles.icon} ${isOpen ? styles.iconOpen : ''}`} size={16} />
      </button>

      {isOpen && (
        <div className={styles.dropdown}>
          {options.map((option, index) => {
            if (option.isSeparator) {
              return (
                <div key={index} className={styles.separator}>
                  {option.label}
                </div>
              );
            }

            return (
              <label key={option.value} className={styles.option}>
                <input
                  type="checkbox"
                  checked={selected.includes(option.value)}
                  onChange={() => handleToggle(option.value)}
                  className={styles.checkbox}
                />
                <span className={styles.optionLabel}>{option.label}</span>
              </label>
            );
          })}
        </div>
      )}
    </div>
  );
}
